import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScenarioThumbnailField } from "@/components/scenarios/ScenarioThumbnailField";
import { RichTextEditor, isHtmlContentEmpty } from "@/components/ui/rich-text-editor";
import {
  useCreateExerciseMutation,
  useGetExerciseFormMetaQuery,
  type CreateExercisePayload,
} from "@/store/apiSlice";

export default function ScenarioCreatePage() {
  const navigate = useNavigate();
  const { data: meta, isLoading: metaLoading, error: metaError } = useGetExerciseFormMetaQuery();
  const [createExercise, { isLoading: creating }] = useCreateExerciseMutation();

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [severityId, setSeverityId] = useState("");
  const [modeId, setModeId] = useState("");
  const [description, setDescription] = useState("");
  const [objective, setObjective] = useState("");
  const [prerequisite, setPrerequisite] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  const canSubmit = useMemo(() => {
    const n = name.trim();
    const c = Number(categoryId);
    const t = Number(typeId);
    return Boolean(n && !Number.isNaN(c) && !Number.isNaN(t));
  }, [name, categoryId, typeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || creating) return;

    const payload: CreateExercisePayload = {
      name: name.trim(),
      category: Number(categoryId),
      type: Number(typeId),
    };
    const s = Number(severityId);
    if (severityId && !Number.isNaN(s)) payload.severity = s;
    const m = Number(modeId);
    if (modeId && !Number.isNaN(m)) payload.mode = m;
    if (!isHtmlContentEmpty(description)) payload.description = description.trim();
    if (!isHtmlContentEmpty(objective)) payload.objective = objective.trim();
    if (!isHtmlContentEmpty(prerequisite)) payload.prerequisite = prerequisite.trim();
    if (thumbnailFile) payload.thumbnail = thumbnailFile;

    try {
      const created = await createExercise(payload).unwrap();
      toast.success("Scenario created.");
      navigate(`/scenarios/${created.id}/detail`, { replace: true });
    } catch (err: unknown) {
      const anyErr = err as { data?: Record<string, unknown>; status?: number };
      const detail =
        typeof anyErr?.data?.detail === "string"
          ? anyErr.data.detail
          : typeof anyErr?.data === "object" && anyErr.data
            ? JSON.stringify(anyErr.data)
            : "Failed to create scenario.";
      toast.error(detail);
    }
  };

  if (metaLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading form…
      </div>
    );
  }

  if (metaError || !meta) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="font-medium text-destructive">Could not load form options.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/scenarios">Back to scenarios</Link>
        </Button>
      </div>
    );
  }

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="space-y-6 ">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/scenarios" className="text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to scenarios
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Add scenario</h1>
        <p className="text-muted-foreground mt-1">
          Create a new exercise. You can configure infrastructure, teams, and flags on the next page.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Exercise details</CardTitle>
          <CardDescription>Required fields are marked. Category and type come from your catalog.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ex-name">Name *</Label>
              <Input
                id="ex-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Scenario title"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ex-cat">Category *</Label>
                <select
                  id="ex-cat"
                  className={selectClass}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {meta.categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ex-type">Exercise type *</Label>
                <select
                  id="ex-type"
                  className={selectClass}
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  required
                >
                  <option value="">Select type</option>
                  {meta.types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.kind})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ex-sev">Severity</Label>
                <select
                  id="ex-sev"
                  className={selectClass}
                  value={severityId}
                  onChange={(e) => setSeverityId(e.target.value)}
                >
                  <option value="">— Optional —</option>
                  {meta.severities.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ex-mode">Mode</Label>
                <select
                  id="ex-mode"
                  className={selectClass}
                  value={modeId}
                  onChange={(e) => setModeId(e.target.value)}
                >
                  <option value="">— Optional —</option>
                  {meta.modes.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <ScenarioThumbnailField
              id="ex-thumbnail"
              value={thumbnailFile}
              onChange={setThumbnailFile}
              disabled={creating}
            />

            <div className="space-y-2">
              <Label>Description</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Short description"
                minEditorHeight="8rem"
              />
            </div>
            <div className="space-y-2">
              <Label>Objective</Label>
              <RichTextEditor
                value={objective}
                onChange={setObjective}
                placeholder="Learning objectives"
                minEditorHeight="8rem"
              />
            </div>
            <div className="space-y-2">
              <Label>Prerequisite</Label>
              <RichTextEditor
                value={prerequisite}
                onChange={setPrerequisite}
                placeholder="Prerequisites for participants"
                minEditorHeight="6rem"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={!canSubmit || creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create scenario
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/scenarios">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
